import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Clock, Send, HelpCircle, Zap, CheckCircle, ArrowRight, MapPin } from "lucide-react";
import { trackContactFormSubmit } from "../lib/analytics";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export default function Contact() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    // Simulate form submission
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // Track contact form submission
      trackContactFormSubmit(formState.subject);
      setIsSubmitted(true);
      setFormState({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact SACVPN - Business VPN Sales & Support</title>
        <meta
          name="description"
          content="Contact SACVPN for business VPN inquiries, sales, and support. Request a demo, get custom pricing for your organization, or reach our team with any question."
        />
        <meta
          name="keywords"
          content="contact business VPN, VPN sales, VPN demo request, corporate VPN support, business VPN inquiry"
        />
        <link rel="canonical" href="https://www.sacvpn.com/contact" />
        <meta property="og:title" content="Contact SACVPN - Business VPN Sales & Support" />
        <meta property="og:description" content="Contact SACVPN for business VPN inquiries, sales, and support. Request a demo, get custom pricing for your organization, or reach our team with any question." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
              Contact
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight max-w-4xl mx-auto"
            >
              Get in <span className="text-brand-400">touch</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed"
            >
              Have a question about SACVPN? Need help with setup? Our support team is here to help.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="lg:col-span-1 space-y-4"
            >
              {/* Email */}
              <motion.div
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Email support</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Reach our team any time and we'll get back to you as soon as we can.
                </p>
                <a
                  href="mailto:support@sacvpn.com"
                  className="text-brand-400 font-medium hover:text-brand-300 transition-colors"
                >
                  support@sacvpn.com
                </a>
              </motion.div>

              {/* Response Time */}
              <motion.div
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Fast response times</h3>
                <p className="text-gray-400 text-sm">
                  US-based support. We read every ticket and reply within 24 hours, often much sooner during business hours (CT).
                </p>
              </motion.div>

              {/* FAQ Link */}
              <motion.div
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                  <HelpCircle className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Check our FAQ</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Find quick answers to common questions.
                </p>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-1.5 text-brand-400 font-medium hover:text-brand-300 transition-colors"
                >
                  Browse FAQ <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>

              {/* Business Inquiries */}
              <motion.div
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Business inquiries</h3>
                <p className="text-gray-400 text-sm mb-3">
                  For enterprise plans and partnerships.
                </p>
                <a
                  href="mailto:support@sacvpn.com"
                  className="text-brand-400 font-medium hover:text-brand-300 transition-colors"
                >
                  support@sacvpn.com
                </a>
              </motion.div>

              {/* Company / mailing address */}
              <motion.div
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-4">
                  <MapPin className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Mailing address</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  SACVPN, a product of StephensCode LLC<br />
                  1135 Grand Central Parkway<br />
                  Conroe, TX 77304, United States
                </p>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:col-span-2"
            >
              <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8 md:p-10">
                <h2 className="text-2xl font-display font-bold text-white tracking-tight mb-2">
                  Send us a message
                </h2>
                <p className="text-gray-400 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-brand-400" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-white tracking-tight mb-3">
                      Message sent
                    </h3>
                    <p className="text-gray-400 max-w-md mx-auto mb-6">
                      Thank you for reaching out. We've received your message and will respond as soon as we can.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="text-brand-400 font-medium hover:text-brand-300 transition-colors"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-300 mb-2"
                        >
                          Your name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formState.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-300 mb-2"
                        >
                          Email address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formState.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-gray-300 mb-2"
                      >
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formState.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-brand-500 transition-colors"
                      >
                        <option value="" className="bg-dark-800">Select a topic</option>
                        <option value="technical" className="bg-dark-800">Technical support</option>
                        <option value="billing" className="bg-dark-800">Billing question</option>
                        <option value="account" className="bg-dark-800">Account issue</option>
                        <option value="sales" className="bg-dark-800">Sales inquiry</option>
                        <option value="partnership" className="bg-dark-800">Partnership</option>
                        <option value="feedback" className="bg-dark-800">Feedback</option>
                        <option value="other" className="bg-dark-800">Other</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-300 mb-2"
                      >
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 transition-colors resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    {error && (
                      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Try SACVPN free for 14 days. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/download"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                View FAQ
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
